// /surplus-rack (2026-10-26, step 2 of the laser packaging plan): every bundle on the surplus rack, surplus and owed,
// and every pull waiting to be MOVED from the rack to an order's pallet. A bundle's label carries a Data Matrix of
// /s/sb-<id>; scanning it lands here with ?bundle=<id> (that bundle first, highlighted, its trail open).
// Every action asks for a PIN and is recorded (jobq-api main.py "SURPLUS RACK": surplus_bundle_events).
// Surplus crates (2026-11-03): each bundle names its crate (beacon) with Find; with crates on, a bundle with no crate
// gets Get a beacon / Use existing crate… (Move to another crate… once racked); a scanned crate beacon lands here with ?crate=<id> (its bundles first).
// Not admin-gated, like /s/:code: the floor works the rack; the PIN says who.
// Part detail (2026-11-04, Mike): the part number on a bundle or a pull to move opens the Sales Order page's part
// inspector (components/PartDetailModal.jsx: picture, size, material, finish, revision, Open part page). Read-only.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import config from "../config";
import pinPrompt from "../utils/pinPrompt";
import { surplusRackCrateLink, surplusRackLink } from "../utils/scanCodes";
import SurplusAllocationModal from "../components/SurplusAllocationModal.jsx";
import { CatalogItemDetailModal, CatalogItemLink } from "../components/PartDetailModal.jsx";
import {
  CrateHint, SurplusBundleActions, SurplusCratesContext, SurplusPullActions, SurplusRefreshContext,
} from "../components/SurplusRack";
import {
  allocateBundle, bundleKindText, cancelPull, labelStatusText, loadBundle, loadRack, openCrate, pullLine, scrapBundle,
  stockBundle,
} from "../features/surplus/surplusBundles";

const C = {
  bg: "#f6f7f9", card: "#ffffff", line: "#dde2e8", ink: "#1d2430", sub: "#5b6675",
  surplus: "#8a6d00", owed: "#b03a2e", stock: "#2f6f9f", ok: "#2e7d32", bad: "#c62828",
};
const KIND_BG = { SURPLUS: "#fff3c4", STOCK: "#dcecf8" };

const page = { background: C.bg, minHeight: "100vh", padding: "1rem 1.25rem 3rem", color: C.ink };
const card = { background: C.card, border: `1px solid ${C.line}`, borderRadius: 10, padding: "0.8rem 1rem" };
const small = { fontSize: "0.9rem", color: C.sub };
const button = (bg, color = "#fff") => ({
  background: bg, color, border: "none", borderRadius: 6, padding: "0.35rem 0.8rem", fontWeight: 700, cursor: "pointer",
});

function day(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** The part inspector's descriptor for a bundle or pull (null = no part id: the number stays plain text). */
export function partItem(x) {
  return x && x.part_id != null ? { kind: "part", id: x.part_id } : null;
}

/** A bundle's / pull's part number: a link to the part's details, at the size of the text around it. */
function PartLink({ x, onInspect, color }) {
  return (
    <CatalogItemLink
      productId={x.product_id}
      item={partItem(x)}
      onInspect={onInspect}
      title={`Part details for ${x.product_id}`}
      style={{ fontSize: "inherit", fontWeight: "inherit", verticalAlign: "baseline", ...(color ? { color } : {}) }}
    />
  );
}

/** True when the bundle matches the filter text (part, description, job, order, bundle #). */
export function bundleMatches(b, text) {
  const q = (text || "").trim().toLowerCase();
  if (!q) return true;
  return [b.product_id, b.part_description, b.master_job_num, b.order_number, b.owed_order_number, `#${b.bundle_id}`,
    String(b.bundle_id)].some((v) => v != null && String(v).toLowerCase().includes(q));
}

function KindBadge({ bundle }) {
  const text = bundleKindText(bundle);
  const owed = bundle.kind === "owed";
  return (
    <span style={{
      background: owed ? C.owed : KIND_BG[text] || "#eee", color: owed ? "#fff" : C.ink, borderRadius: 5,
      padding: "0.1rem 0.5rem", fontWeight: 800, fontSize: "0.85rem", letterSpacing: "0.03em",
    }}>{text}</span>
  );
}

function Trail({ bundleId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    loadBundle(bundleId).then((d) => alive && setData(d)).catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [bundleId]);
  if (err) return <div style={{ ...small, color: C.bad }}>{err}</div>;
  if (!data) return <div style={small}>Loading the trail…</div>;
  return (
    <ul style={{ margin: "0.4rem 0 0", paddingLeft: "1.1rem", ...small }} data-testid="bundle-trail">
      {data.events.map((e) => (
        <li key={e.event_id}>
          {new Date(e.created_at).toLocaleString()}: <b>{e.action.replace(/_/g, " ")}</b>
          {e.qty != null ? ` ${e.qty} pcs` : ""}{e.target_order_number ? ` → ${e.target_order_number}` : ""}
          {e.user_name ? ` · ${e.user_name}` : ""}{e.note ? ` · ${e.note}` : ""}
        </li>
      ))}
    </ul>
  );
}

function BundleRow({ b, focused, onAllocate, onChanged, cratesOn, onInspect }) {
  const [open, setOpen] = useState(focused);
  const crate = openCrate(b);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);
  const label = labelStatusText(b.label_status);
  useEffect(() => { if (focused) setOpen(true); }, [focused]);   // a later scan of this bundle opens its trail

  const act = async (what, fn) => {
    if (busy) return;
    setBusy(true);
    try {
      const pin = await pinPrompt(`Your PIN to ${what} #${b.bundle_id} (${b.product_id}):`, { autofill: false });
      if (!pin) return;
      await fn(pin);
      setNote({ tone: "ok", text: "Recorded" });
      onChanged();
    } catch (e) {
      setNote({ tone: "bad", text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const onScrap = async () => {
    const raw = window.prompt(`How many of the ${b.free_qty} free pcs of #${b.bundle_id} are scrap?`, String(b.free_qty));
    const qty = parseInt(raw, 10);
    if (!qty || qty <= 0) return;
    const why = window.prompt("Why are they scrapped? (bent, wrong size, rusted…)");
    if (!why || !why.trim()) return;
    await act("scrap pieces of", (pin) => scrapBundle(b.bundle_id, pin, qty, why.trim()));
  };

  return (
    <div
      data-testid={`bundle-${b.bundle_id}`}
      style={{ ...card, borderColor: focused ? "#1f6feb" : C.line, borderWidth: focused ? 3 : 1 }}
    >
      <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ minWidth: "6rem" }}>
          <div style={{ fontFamily: "monospace", fontSize: "1.3rem", fontWeight: 800 }}>#{b.bundle_id}</div>
          <KindBadge bundle={b} />
        </div>
        <div style={{ flex: 1, minWidth: "14rem" }}>
          <div style={{ fontSize: "1.15rem", fontWeight: 700 }}>
            {b.on_rack_qty} pcs <PartLink x={b} onInspect={onInspect} />
            {b.pending_qty > 0 && <span style={{ color: C.surplus }}> · {b.pending_qty} promised</span>}
            {b.scrapped_qty > 0 && <span style={small}> · {b.scrapped_qty} scrapped</span>}
          </div>
          <div style={small}>{b.part_description}</div>
          <CrateHint bundle={b} dark={false} />
          <div style={small}>
            Job {b.master_job_num || "?"}{b.order_number ? ` · cut on ${b.order_number}` : ""}
            {b.customer_name ? ` (${b.customer_name})` : ""} · {day(b.created_at)}{b.heat_text ? ` · ${b.heat_text}` : ""}
          </div>
          <div style={small}>
            {b.racked
              ? `${crate ? `In crate ${crate.beacon_id}` : "On the rack"} since ${day(b.racked_at)}${b.racked_by_name ? ` · ${b.racked_by_name}` : ""}`
              : "NOT racked yet"}
            {" · "}<span style={{ color: label.tone === "ok" ? C.ok : C.bad }}>{label.text}</span>
            {b.stock ? ` · kept as stock by ${b.stock_by_name || "?"}` : ""}
          </div>
        </div>
        <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", alignItems: "flex-start" }}>
          {b.kind === "surplus" && b.free_qty > 0 && (
            <button type="button" style={button("#1f6feb")} onClick={() => onAllocate(b)}>Allocate to an order</button>
          )}
          {b.free_qty > 0 && <button type="button" style={button(C.bad)} disabled={busy} onClick={onScrap}>Scrap</button>}
          {b.kind === "surplus" && (
            <button
              type="button"
              style={button(C.stock)}
              disabled={busy}
              onClick={() => act(b.stock ? "stop keeping as stock" : "keep as stock", (pin) => stockBundle(b.bundle_id, pin, !!b.stock))}
            >
              {b.stock ? "Not stock" : "Keep as stock"}
            </button>
          )}
          <button type="button" style={button("#5b6675")} onClick={() => setOpen((v) => !v)}>{open ? "Hide trail" : "Trail"}</button>
        </div>
      </div>
      {(!b.racked || cratesOn || crate) && b.on_rack_qty > 0 && (
        <div style={{ marginTop: "0.4rem", background: "#2b2f36", borderRadius: 8, padding: "0.5rem" }}><SurplusBundleActions bundle={b} /></div>
      )}
      {note && <div role="status" style={{ ...small, color: note.tone === "ok" ? C.ok : C.bad, fontWeight: 700 }}>{note.text}</div>}
      {open && <Trail bundleId={b.bundle_id} />}
    </div>
  );
}

function PullRow({ p, onChanged, onInspect }) {
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);
  const onUndo = async () => {
    if (busy) return;
    const what = p.allocation_id && p.live !== false
      ? `cancel the allocation of ${pullLine(p)}`
      : `undo ${pullLine(p)} (an owed bundle with nothing left to move becomes SURPLUS)`;
    setBusy(true);
    try {
      const pin = await pinPrompt(`Your PIN to ${what}:`, { autofill: false });
      if (!pin) return;
      await cancelPull(p.pull_id, pin);
      onChanged();
    } catch (e) {
      setNote(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={`pull-${p.pull_id}`} style={{ ...card, background: "#2b2f36", color: "#e8ecf2", borderColor: "#2b2f36" }}>
      <div style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: "14rem" }}>
          <div style={{ fontSize: "1.15rem", fontWeight: 800 }}>
            {/* pullLine's words, with the part number as a link */}
            {p.qty} pcs {p.product_id ? <PartLink x={p} onInspect={onInspect} color="#8ec5ff" /> : null}
            {" "}from #{p.bundle_id} → {p.target_order_number || "its order"}
          </div>
          <div style={{ fontSize: "0.9rem", color: "#b9c2cf" }}>
            {p.kind === "owed" ? "Owed" : "Allocated"} {day(p.created_at)}{p.created_by_name ? ` by ${p.created_by_name}` : ""}
          </div>
        </div>
        <SurplusPullActions pull={p} onDone={onChanged} />
        <button type="button" style={button("#5b6675")} disabled={busy} onClick={onUndo}>Undo</button>
      </div>
      {note && <div role="status" style={{ color: "#ff8a8a", fontWeight: 700 }}>{note}</div>}
    </div>
  );
}

export default function SurplusRackPage() {
  const location = useLocation();
  const focusId = surplusRackLink(location.search);
  const focusCrate = surplusRackCrateLink(location.search);
  const [rack, setRack] = useState(null);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("");
  const [showClosed, setShowClosed] = useState(false);
  const [allocating, setAllocating] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [allocNote, setAllocNote] = useState(null);
  const [inspected, setInspected] = useState(null);   // {kind: 'part', id} open in the part inspector
  const closeInspect = useCallback(() => setInspected(null), []);

  const refresh = useCallback(() => {
    loadRack({ includeClosed: showClosed }).then((d) => { setRack(d); setErr(null); }).catch((e) => setErr(e.message));
  }, [showClosed]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    fetch(`${config.API_BASE_URL}/customers`).then((r) => r.json()).then((d) => setCustomers(Array.isArray(d) ? d : []))
      .catch(() => setCustomers([]));
  }, []);

  const bundles = useMemo(() => {
    const list = (rack && rack.bundles) || [];
    const hit = (b) => b.bundle_id === focusId || (focusCrate != null && b.crate_id === focusCrate);
    const shown = list.filter((b) => bundleMatches(b, filter) || hit(b));
    return [...shown].sort((a, b) => hit(b) - hit(a));
  }, [rack, filter, focusId, focusCrate]);
  // The modal re-reads its target orders whenever `context` changes: one object per bundle picked.
  const allocContext = useMemo(() => (allocating ? {
    part_id: allocating.part_id, part_product_id: allocating.product_id, part_description: allocating.part_description,
    // a bundle's pieces are capped at each target line's remaining (the API caps them too)
    surplus: allocating.free_qty, source_so_line_id: -1,
  } : null), [allocating]);
  const pending = ((rack && rack.pulls) || []).filter((p) => p.status === "pending");
  const recent = ((rack && rack.pulls) || []).filter((p) => p.status === "moved");

  const onConfirmAllocate = async (allocs) => {
    const b = allocating;
    setAllocating(null);
    if (!b || !allocs || !allocs.length) return;
    const pin = await pinPrompt(`Your PIN to allocate pieces of #${b.bundle_id} (${b.product_id}):`, { autofill: false });
    if (!pin) return;
    const said = [];
    for (const a of allocs) {
      try {
        await allocateBundle(b.bundle_id, pin, a);
        said.push(`${a.quantity} → ${a.target_so_number || a.target_so_id}: now MOVE them to its pallet`);
      } catch (e) {
        said.push(`${a.quantity} → ${a.target_so_number || a.target_so_id}: ${e.message}`);
      }
    }
    setAllocNote(said.join(" · "));
    refresh();
  };

  if (err) return <div style={page}><h2>Surplus rack</h2><div style={{ color: C.bad }}>{err}</div></div>;
  if (!rack) return <div style={page}><h2>Surplus rack</h2><div style={small}>Loading…</div></div>;
  if (!rack.set_up) return <div style={page}><h2>Surplus rack</h2><div style={small}>The surplus rack isn't set up on this server yet.</div></div>;

  return (
    <SurplusRefreshContext.Provider value={refresh}>
      <SurplusCratesContext.Provider value={!!rack.crates_on}>
      <div style={page} data-testid="surplus-rack-page">
        <div style={{ display: "flex", alignItems: "baseline", gap: "1rem", flexWrap: "wrap" }}>
          <h2 style={{ margin: 0 }}>Surplus rack</h2>
          <span style={small}>
            {bundles.length} bundle{bundles.length === 1 ? "" : "s"} · {pending.length} to move. Pieces count on an order's pallet
            only once someone moves them and clicks <b>Moved</b>.
          </span>
        </div>

        {allocNote && <div role="status" style={{ ...card, marginTop: "0.8rem", borderColor: C.surplus }}>{allocNote}</div>}

        <h3 style={{ marginTop: "1.2rem" }}>Move from the rack to a pallet ({pending.length})</h3>
        {pending.length === 0 && <div style={small}>Nothing waiting to move.</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {pending.map((p) => <PullRow key={p.pull_id} p={p} onChanged={refresh} onInspect={setInspected} />)}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginTop: "1.4rem", flexWrap: "wrap" }}>
          <h3 style={{ margin: 0 }}>On the rack</h3>
          <input
            aria-label="Filter the rack"
            placeholder="Part, job, order or #"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ padding: "0.35rem 0.6rem", borderRadius: 6, border: `1px solid ${C.line}`, minWidth: "16rem" }}
          />
          <label style={small}>
            <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} /> show emptied bundles
          </label>
        </div>
        {bundles.length === 0 && <div style={{ ...small, marginTop: "0.5rem" }}>Nothing on the rack{filter ? " matches" : ""}.</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "0.6rem" }}>
          {bundles.map((b) => (
            <BundleRow
              key={b.bundle_id} b={b} cratesOn={!!rack.crates_on}
              focused={b.bundle_id === focusId || (focusCrate != null && b.crate_id === focusCrate)}
              onAllocate={setAllocating} onChanged={refresh} onInspect={setInspected}
            />
          ))}
        </div>

        {recent.length > 0 && (
          <>
            <h3 style={{ marginTop: "1.4rem" }}>Moved in the last 7 days</h3>
            <ul style={small}>
              {recent.map((p) => (
                <li key={p.pull_id}>
                  {day(p.moved_at)}: {pullLine(p)} · {p.moved_by_name}
                </li>
              ))}
            </ul>
          </>
        )}

        <SurplusAllocationModal
          show={!!allocating}
          onClose={() => setAllocating(null)}
          context={allocContext}
          currentSoId={allocating ? allocating.sales_order_id : null}
          sourceCustomerId={null}
          customerList={customers}
          onConfirm={onConfirmAllocate}
        />
        <CatalogItemDetailModal apiBase={config.API_BASE_URL} item={inspected} onClose={closeInspect} />
      </div>
      </SurplusCratesContext.Provider>
    </SurplusRefreshContext.Provider>
  );
}
